// Wire userData ve sahnedeki wireTitle'lardan etiket metni üretir.
// Etiket tipleri: 'numara' | 'panoNo' | 'bundle'.

// Sahnedeki wireTitle gruplarını wireId → exitIndex+1 map'ine çevir.
// panelExitManager wireTitle'ları yaratırken titleGroup.userData.exitIndex ve
// label.userData.outSnapID = wireId set ediyor.
function buildWireNumberMap(scene) {
  const map = new Map();
  if (!scene) return map;
  scene.traverse(obj => {
    if (obj.userData?.type !== 'wireTitle') return;
    const exitIdx = obj.userData?.exitIndex;
    if (exitIdx == null) return;
    let outSnapID = null;
    obj.traverse(c => {
      if (outSnapID != null) return;
      if (c.userData?.outSnapID) outSnapID = c.userData.outSnapID;
    });
    if (outSnapID != null) map.set(String(outSnapID), exitIdx + 1);
  });
  return map;
}

function getWireNumber(wire, numberMap) {
  if (!wire) return null;
  const id = wire.userData?.id;
  if (id != null) {
    const n = numberMap.get(String(id));
    if (n != null) return n;
  }
  // Fallback: bazı wire'larda lineId saklı
  const lineId = wire.userData?.lineId;
  if (lineId != null) {
    const n = numberMap.get(String(lineId));
    if (n != null) return n;
  }
  return null;
}

function getPanelName(wire) {
  if (!wire) return null;
  return (
    wire.userData?.panelName ||
    wire.userData?.sourcePanelName ||
    null
  );
}

// Bundle wire'ları için: bundle.userData.wireIds → her wire'ın numarası.
function getBundleWireNumbers(bundle, scene, numberMap) {
  const ids = bundle?.userData?.wireIds || [];
  const nums = [];
  // wireId → wire object map
  const wireById = new Map();
  scene.traverse(obj => {
    const wid = obj?.userData?.id;
    if (wid != null && obj.isLine) wireById.set(String(wid), obj);
  });
  for (const id of ids) {
    const w = wireById.get(String(id));
    const n = getWireNumber(w, numberMap);
    if (n != null) nums.push(n);
  }
  return nums.sort((a, b) => a - b);
}

function getBundleSourcePanel(bundle, scene) {
  const ids = bundle?.userData?.wireIds || [];
  const wireById = new Map();
  scene.traverse(obj => {
    const wid = obj?.userData?.id;
    if (wid != null && obj.isLine) wireById.set(String(wid), obj);
  });
  for (const id of ids) {
    const w = wireById.get(String(id));
    const name = getPanelName(w);
    if (name) return name;
  }
  return null;
}

// Bundle içindeki her wire için { id, panelName, wireNumber } dizisi döner.
export function getBundleWireInfos(scene, bundle) {
  const ids = bundle?.userData?.wireIds || [];
  if (ids.length === 0) return [];

  const numberMap = buildWireNumberMap(scene);

  // wireId → wire objesi
  const wireById = new Map();
  // panelId → panel adı
  const panelNameById = new Map();
  // wireId → { panelName, exitIndex } — shaftExitPoint üzerinden
  const exitPointMap = new Map();

  scene.traverse(obj => {
    const ud = obj.userData || {};

    // Wire nesneleri
    if (obj.isLine && ud.id != null) wireById.set(String(ud.id), obj);

    // Panel nesneleri — panelID → name
    if (ud.name && (ud.panelID || ud.id) && !obj.isLine) {
      const pid = String(ud.panelID || ud.id);
      if (!panelNameById.has(pid)) panelNameById.set(pid, ud.name);
    }

    // shaftExitPoint — wireId → { exitIndex, shaftId }
    if (ud.type === 'shaftExitPoint' && ud.wireId != null) {
      exitPointMap.set(String(ud.wireId), { exitIndex: ud.exitIndex, shaftId: String(ud.shaftId ?? '') });
    }
  });

  return ids.map(id => {
    const w = wireById.get(String(id));
    const ud = w?.userData || {};

    // Panel adı: wire.panelName → wire.sourcePanelName → panelID üzerinden panel
    let panelName = ud.panelName || ud.sourcePanelName || null;
    if (!panelName) {
      const pid = String(ud.panelID || ud.sourcePanelId || '');
      if (pid) panelName = panelNameById.get(pid) || null;
    }
    // shaftExitPoint üzerinden de dene
    if (!panelName) {
      const ep = exitPointMap.get(String(id));
      if (ep?.shaftId) panelName = panelNameById.get(ep.shaftId) || null;
    }

    // Wire numarası: wireTitle map → shaftExitPoint exitIndex
    let wireNumber = (w ? getWireNumber(w, numberMap) : null) ?? null;
    if (wireNumber == null) {
      const ep = exitPointMap.get(String(id));
      if (ep?.exitIndex != null) wireNumber = ep.exitIndex + 1;
    }

    return { id, panelName, wireNumber };
  });
}

// Verilen wire/bundle ve type için etiket metnini üretir. Veri eksikse null döner.
export function computeLabelText(scene, target, type) {
  if (!target) return null;
  const isBundleRep = !!target.userData?.isBundleRep;
  const numberMap = buildWireNumberMap(scene);

  if (type === 'numara') {
    if (isBundleRep) {
      // Bundle için "numara" tipi → ilk-son wire numarası kısayolu
      let parent = target.parent;
      while (parent && parent.userData?.type !== 'wireBundle') parent = parent.parent;
      if (!parent) return null;
      const nums = getBundleWireNumbers(parent, scene, numberMap);
      if (nums.length === 0) return null;
      return nums.length === 1 ? `${nums[0]}` : `${nums[0]}-${nums[nums.length - 1]}`;
    }
    const n = getWireNumber(target, numberMap);
    return n != null ? String(n) : null;
  }

  if (type === 'panoNo') {
    if (isBundleRep) {
      let parent = target.parent;
      while (parent && parent.userData?.type !== 'wireBundle') parent = parent.parent;
      if (!parent) return null;
      const panelName = getBundleSourcePanel(parent, scene);
      const nums = getBundleWireNumbers(parent, scene, numberMap);
      if (!panelName || nums.length === 0) return null;
      const range = nums.length === 1 ? `${nums[0]}` : `${nums[0]}-${nums[nums.length - 1]}`;
      return `${panelName} ${range}`;
    }
    const panelName = getPanelName(target);
    const n = getWireNumber(target, numberMap);
    if (!panelName || n == null) return null;
    return `${panelName} no-${n}`;
  }

  if (type === 'bundle') {
    if (!isBundleRep) return null;
    let parent = target.parent;
    while (parent && parent.userData?.type !== 'wireBundle') parent = parent.parent;
    if (!parent) return null;
    const nums = getBundleWireNumbers(parent, scene, numberMap);
    if (nums.length === 0) return null;
    const range = nums.length === 1 ? `${nums[0]}` : `${nums[0]}-${nums[nums.length - 1]}`;
    const panelName = getBundleSourcePanel(parent, scene);
    return panelName ? `${panelName} ${range}` : range;
  }

  return null;
}
